import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { date, money } from "@/lib/api";
import { themeFor, loadLogo, header, infoBlock, photoForPdf } from "@/lib/pdf";

const GREY = [120, 133, 151], INK = [23, 34, 54], LIGHT = [241, 245, 250];
const PAGE_BOTTOM = 280;

const FIELDS = [
  ["Jenis pekerjaan", r => r.work_type], ["Pekerjaan dikerjakan", r => r.work_completed], ["Status pekerjaan", r => r.work_status],
  ["Progress", r => r.progress_type === "Volume" && r.progress_volume ? `${r.progress_volume} (${r.progress}%)` : `${r.progress ?? 0}%`],
  ["Manpower", r => r.manpower != null ? `${r.manpower} orang` : ""], ["Cuaca", r => r.weather],
  ["Material dipakai", r => r.material_used], ["Kekurangan material", r => r.material_shortage], ["Alat digunakan", r => r.equipment_used],
  ["Masalah / kendala", r => r.problems], ["Solusi", r => r.solution], ["Catatan", r => r.notes], ["Safety", r => r.safety_incident],
  ["Dilaporkan oleh", r => r.author],
];

function ensure(doc, y, need) { if (y + need > PAGE_BOTTOM) { doc.addPage(); return 20; } return y; }

async function drawPhotos(doc, y, photos, theme) {
  const BW = 42, BH = 32, GAP = 3; let col = 0;
  y = ensure(doc, y, BH + 8);
  doc.setFontSize(7.5); doc.setTextColor(...GREY); doc.text(`Foto lapangan (${photos.length})`, 18, y); y += 2;
  for (const p of photos.slice(0, 8)) {
    const img = await photoForPdf(p.id, 700); if (!img) continue;
    if (col === 4) { col = 0; y += BH + GAP; y = ensure(doc, y, BH + 4); }
    const x = 18 + col * (BW + GAP);
    let w = BW, h = BW / img.ratio; if (h > BH) { h = BH; w = BH * img.ratio; }
    doc.setDrawColor(220, 226, 235); doc.setFillColor(255, 255, 255); doc.rect(x, y, BW, BH, "FD");
    doc.addImage(img.src, "JPEG", x + (BW - w) / 2, y + (BH - h) / 2, w, h);
    col++;
  }
  return y + BH + 6;
}

async function drawReport(doc, y, r, projectName, theme, photos) {
  const rows = FIELDS.map(([k, f]) => [k, f(r)]).filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== "");
  y = ensure(doc, y, 22);
  doc.setFillColor(...LIGHT); doc.rect(14, y, 182, 8, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(9.5); doc.setTextColor(...theme.primary); doc.text(projectName, 17, y + 5.5);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...GREY); doc.text(`${r.id} · ${r.progress ?? 0}% · ${r.manpower ?? 0} orang`, 193, y + 5.5, { align: "right" });
  y += 10;
  autoTable(doc, { startY: y, margin: { left: 14, right: 14 }, theme: "plain", styles: { fontSize: 8.3, cellPadding: { top: 1.4, bottom: 1.4, left: 2, right: 2 }, textColor: INK, lineColor: [232, 236, 241], lineWidth: 0.1 },
    body: rows, columnStyles: { 0: { cellWidth: 42, textColor: GREY, fontStyle: "bold" }, 1: { cellWidth: 140 } }, pageBreak: "auto" });
  y = doc.lastAutoTable.finalY + 4;
  if (photos?.length) y = await drawPhotos(doc, y, photos, theme);
  return y + 4;
}

export async function exportDailyRecapPDF(reports, { start, end, company, projects = [], projectName = "", photosByReport = null }) {
  const theme = themeFor(company, "report"), logo = await loadLogo(company);
  const doc = new jsPDF({ compress: true });
  const pname = (id) => projects.find(p => p.id === id)?.name || id;
  const sorted = [...reports].sort((a, b) => a.date.localeCompare(b.date) || pname(a.project_id).localeCompare(pname(b.project_id)));
  header(doc, company, "REKAP DAILY REPORT", `${date(start)} — ${date(end)}`, theme, logo);
  const days = [...new Set(sorted.map(r => r.date))];
  const manpower = sorted.reduce((s, r) => s + (Number(r.manpower) || 0), 0);
  const avgProg = sorted.length ? (sorted.reduce((s, r) => s + (Number(r.progress) || 0), 0) / sorted.length).toFixed(1) : "0";
  const issues = sorted.filter(r => (r.problems || "").trim()).length, incidents = sorted.filter(r => r.safety_incident && r.safety_incident !== "Tidak ada").length;
  let y = infoBlock(doc, 46,
    [["Periode", `${date(start)} — ${date(end)}`], ["Project", projectName || "Semua project"], ["Hari terlapor", `${days.length} hari`], ["Jumlah laporan", `${sorted.length} laporan`]],
    [["Manpower", `${manpower} orang-hari`], ["Rata2 progress", `${avgProg}%`], ["Ada kendala", `${issues} laporan`], ["Insiden safety", `${incidents}`]], theme);

  autoTable(doc, { startY: y, margin: { left: 14, right: 14 }, styles: { fontSize: 8, cellPadding: 2 }, headStyles: { fillColor: theme.primary, textColor: 255, fontSize: 7.5 },
    head: [["Tanggal", "Project", "Jenis pekerjaan", "Pekerjaan", "Orang", "Progress", "Cuaca", "Kendala"]],
    body: sorted.map(r => [date(r.date), pname(r.project_id), r.work_type || "-", r.work_completed || "-", `${r.manpower ?? 0}`, `${r.progress ?? 0}%`, r.weather || "-", (r.problems || "").trim() ? "Ya" : "-"]),
    columnStyles: { 0: { cellWidth: 21 }, 1: { cellWidth: 30 }, 2: { cellWidth: 26 }, 3: { cellWidth: 40 }, 4: { cellWidth: 14, halign: "center" }, 5: { cellWidth: 17, halign: "center" }, 6: { cellWidth: 17 }, 7: { cellWidth: 17, halign: "center" } } });
  y = doc.lastAutoTable.finalY + 10;

  doc.addPage(); y = 20;
  doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(...theme.primary); doc.text("DETAIL LAPORAN PER HARI", 14, y); y += 8;
  for (const d of days) {
    const dayReports = sorted.filter(r => r.date === d);
    y = ensure(doc, y, 30);
    doc.setFillColor(...theme.primary); doc.rect(14, y, 182, 7.5, "F");
    doc.setTextColor(255); doc.setFont("helvetica", "bold"); doc.setFontSize(9.5);
    doc.text(new Date(d + "T00:00:00").toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" }), 17, y + 5.2);
    doc.setFont("helvetica", "normal"); doc.setFontSize(8);
    doc.text(`${dayReports.length} laporan · ${dayReports.reduce((s, r) => s + (Number(r.manpower) || 0), 0)} orang`, 193, y + 5.2, { align: "right" });
    y += 10;
    for (const r of dayReports) y = await drawReport(doc, y, r, pname(r.project_id), theme, photosByReport?.[r.id]);
  }
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) { doc.setPage(i); doc.setFontSize(7.5); doc.setTextColor(...GREY); doc.text(`Rekap Daily Report ${date(start)} — ${date(end)} · Dicetak ${new Date().toLocaleString("id-ID")} · CONTRACTOR ERP`, 14, 290); doc.text(`Hal. ${i}/${pages}`, 196, 290, { align: "right" }); }
  doc.save(`DAILY-REPORT-${start}_${end}${projectName ? `-${projectName.replaceAll(" ", "_")}` : ""}.pdf`);
}
